import React, { useState, useEffect } from 'react';
import { 
  Leaf, Microscope, BookOpen, TrendingUp, Filter, Search, Eye, Clock, 
  MessageCircle, Heart, Bookmark, Share2, ChevronDown, ChevronRight,
  Award, Users, Calendar, Tag, ExternalLink, Download, Play,
  Beaker, Sprout, FileText, Globe, Star, ThumbsUp, ArrowRight,
  Video, PieChart, BarChart3, Activity, Bell, Sparkles, Trophy,
  Flame, Zap, X, Plus, ChevronUp, Target, Crown, Coffee, Menu
} from 'lucide-react';
import PublicProfileView from '../profile_component/PublicProfileViewPage';
import { AchievementsCard } from '../components/AchievementsCard';
import { CommunityStatsCard } from '../components/CommunityStats';
import { TrendingCard } from '../components/TrendingCard';
import ResearchArticleDetail from './ResearchArticlePage';
import { useTheme } from '../arrangement/ThemeContext';
import { useUser } from '../context/UserContext';
import SubmitResearchDialog from '../components/SubmitResearch';
import { researchAPI } from '../services/ResearchServices';
import { UserLevelCard } from '../components/UserLevelCard';
import API_URL from '../config/ApiUrl';

// Mobile Stats Toggle Component (similar to threads page)
const MobileStatsToggle = ({ profileData, achievementsLoading, isAuthenticated, trendingTopics, colors }) => {
  const [activeTab, setActiveTab] = useState('trending');

  return (
    <div style={{
      background: colors.bgTertiary,
      borderRadius: '16px',
      border: '1px solid #333',
      overflow: 'hidden'
    }}>
      {/* Toggle Header */}
      <div style={{
        display: 'flex',
        background: colors.bgCard,
        borderBottom: '1px solid #333'
      }}>
        <button
          onClick={() => setActiveTab('trending')}
          style={{
            flex: 1,
            padding: '12px 16px',
            background: activeTab === 'trending' 
              ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' 
              : 'transparent',
            color: activeTab === 'trending' ? 'white' : '#888',
            border: 'none',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          📈 Trending
        </button>
        <button
          onClick={() => setActiveTab('stats')}
          style={{
            flex: 1,
            padding: '12px 16px',
            background: activeTab === 'stats' 
              ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' 
              : 'transparent',
            color: activeTab === 'stats' ? 'white' : '#888',
            border: 'none',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          👥 Community
        </button>
        <button
          onClick={() => setActiveTab('achievements')}
          style={{
            flex: 1,
            padding: '12px 16px',
            background: activeTab === 'achievements' 
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
              : 'transparent',
            color: activeTab === 'achievements' ? 'white' : '#888',
            border: 'none',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          🏆 Progress
        </button>
      </div>

      {/* Content Area */}
      <div style={{ 
        padding: '0',
        background: 'transparent'
      }}>
        {activeTab === 'trending' && (
          <TrendingCard topics={trendingTopics} />
        )}
        {activeTab === 'stats' && (
          <CommunityStatsCard />
        )}
        {activeTab === 'achievements' && (
          <AchievementsCard 
            profileData={profileData} 
            loading={achievementsLoading} 
            isAuthenticated={isAuthenticated} 
          />
        )}
      </div>
    </div>
  );
};

const ResearchHub = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('hot');
  const [notifications, setNotifications] = useState(5);
  const [savedArticles, setSavedArticles] = useState(new Set());
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const { user, isAuthenticated, } = useUser();
  const { colors } = useTheme();

  const [showProfileView, setShowProfileView] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  

  const [currentPage, setCurrentPage] = useState(1);
  const [articlesPerPage, setArticlesPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);

  const [tags, setTags] = useState([]);
  const [tagCounts, setTagCounts] = useState({});
  const [tagsLoading, setTagsLoading] = useState(true);

  const [headerStats, setHeaderStats] = useState({
    members: 0,
    studies: 0,
    contributions: 0
  });
  const [profileData, setProfileData] = useState({ achievements: [] });
  const [statsLoading, setStatsLoading] = useState(true);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);

  const [articles, setArticles] = useState([]);
  const [featuredStudy, setFeaturedStudy] = useState(null);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [totalArticles, setTotalArticles] = useState(0);
  const [achievementsLoading, setAchievementsLoading] = useState(true);
  
  const [userStats, setUserStats] = useState(null);

  // Mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Add resize listener
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  
  const getCurrentUserId = () => {
    return user?.id || null;
  };

  const fetchTags = async () => {
    try {
      setTagsLoading(true);
      const response = await fetch(`${API_URL}/api/research/tags`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (result.success) {
        setTags(result.data.tags);
        setTagCounts(result.data.tag_counts || {});
      } else {
        console.error('Failed to fetch tags:', result.error);
        setTags([]);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    } finally {
      setTagsLoading(false);
    }
  };

  const handleUsernameClick = (userId) => {
    console.log('====================HANDLE GET CLICK ========================')
    console.log(userId)
    console.log(getCurrentUserId())
    console.log('====================HANDLE GET CLICK ========================')

    if (userId) {
      setSelectedUserId(userId);
      setShowProfileView(true);
    }
  };

  const fetchAchievements = async () => {
    try {
      setAchievementsLoading(true);
      const response = await fetch(`${API_URL}/api/achievements`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (result.success) {
        setProfileData(result.data);
      } else {
        console.error('Failed to fetch achievements:', result.error);
        setProfileData({ achievements: [] });
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
      setProfileData({ achievements: [] });
    } finally {
      setAchievementsLoading(false);
    }
  };
  

  useEffect(() => {
    fetchUserStats();
    loadInitialData();
    fetchAchievements();
    fetchTags();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    loadArticles(1, true);
  }, [activeCategory, searchTerm, sortBy, selectedTag, articlesPerPage]);

  useEffect(() => {
    const loadHeaderStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/research/header-stats`);
        const json = await res.json();
        if (json.success) {
          setHeaderStats(json.data);
        }
      } catch (e) {
        console.error('Failed to load header stats', e);
      }
    };

    loadHeaderStats();
  }, []);

  useEffect(() => {
    console.log(":HERE I AM ")
  const loadUserVotes = async () => {
    try {
      const votes = await researchAPI.getUserArticleVotes();

      const voteMap = new Map();
      votes.forEach(vote => {
        if (vote.vote_type) {
          voteMap.set(vote.article_id, vote.vote_type);
        }
      });

      setVotedArticles(voteMap);
    } catch (error) {
      console.error('Failed to load user votes:', error);
    }
  };

  loadUserVotes();
}, []);


useEffect(() => {
      const style = document.createElement('style');
      style.textContent = `
        .clickable-username {
          position: relative;
          cursor: pointer;
          transition: color 0.3s ease;
        }
        
        .clickable-username::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 1px;
          background: linear-gradient(135deg, #00d4aa 0%, #10b981 100%);
          transition: width 0.3s ease;
        }
        
        .clickable-username:hover {
          color: #00d4aa !important;
        }
        
        .clickable-username:hover::after {
          width: 100%;
        }
      `;
      document.head.appendChild(style);
      return () => {
        if (style.parentNode) {
          document.head.removeChild(style);
        }
      };
    }, []);
    

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [articlesRes, featuredRes, categoriesRes] = await Promise.all([
        researchAPI.getArticles(),
        researchAPI.getFeaturedArticle(),
        researchAPI.getCategories(),
      ]);

      setArticles(articlesRes.articles);
      setFeaturedStudy(featuredRes.featured_article || null);
      
      setCategories(categoriesRes.categories);
      setTotalArticles(articlesRes.pagination.total);
      setHasNextPage(articlesRes.pagination.has_next);
      console.log('==========================================')
    } catch (error) {
      console.error('Failed to load initial data:', error);
      setError('Failed to load research data');
    } finally {
      setLoading(false);
    }
  };


  const loadArticles = async (page = 1, reset = false) => {
    try {
      const params = {
        page: page,
        per_page: articlesPerPage,
        category: activeCategory,
        search: searchTerm,
        sort_by: sortBy,
        tag: selectedTag
      };

      const response = await researchAPI.getArticles(params);
      
      if (reset || page === 1) {
        setArticles(response.articles);
      } else {
        setArticles(prev => [...prev, ...response.articles]);
      }
      
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.pages);
      setHasNextPage(response.pagination.has_next);
      setHasPrevPage(response.pagination.has_prev);
      setTotalArticles(response.pagination.total);
      
    } catch (error) {
      console.error('Failed to load articles:', error);
      setError('Failed to load articles');
    }
  };

  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showArticleDetail, setShowArticleDetail] = useState(false);

  const [votedArticles, setVotedArticles] = useState(new Map());
  const fetchUserStats = async () => {
    console.log('HEREEEEEE')
    try {
      setStatsLoading(true);
      console.log('HEREEEEEE')
      const response = await fetch(`${API_URL}/api/user/stats`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.log('HEREEEEEE')

      const result = await response.json();
      console.log('HEREEEEEE',result)
      if (result.success) {
        setUserStats(result.data);
      } else {
        console.error('Failed to fetch user stats:', result.error);
        setUserStats({
          level: 1,
          xp: 0,
          nextLevelXp: 100,
          get_next_level_threshold: 100,
          streak: 0,
          badges: [],
          reputation: 0,
          posts: 0,
          comments: 0,
          upvotes: 0,
          threads: 0,
          reviews: 0,
          helpful_answers: 0,
          sesh_rooms_joined: 0
        });
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      setUserStats({
        level: 1,
        xp: 0,
        nextLevelXp: 100,
        get_next_level_threshold: 100,
        streak: 0,
        badges: [],
        reputation: 0,
        posts: 0,
        comments: 0,
        upvotes: 0,
        threads: 0,
        reviews: 0,
        helpful_answers: 0,
        sesh_rooms_joined: 0
      });
    } finally {
      setStatsLoading(false);
    }
  };

      const getSmokerEmoji = (level) => {
      switch(level) {
        case 'beginner': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/cdb53b7a8c7e4624a810c0d654ca03c8_Smoker_beginer.webp';
        case 'casual': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/e72017f33d1c480db1928ca3d7295de4_Smoker_Level_cas.webp';
        case 'connoisseur': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/3e1e13711dd441fcb7c34c155416ee69_Smoker_Level_con.webp';
        case 'stoner': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/9084554e27d747188148530bc7899eee_Smoker_Level_Logo_Stoner.webp';
        default: return  'https://highmind-bucket.s3.us-east-1.amazonaws.com/6d4ba06007624c3691fd682dfbb31cdf_hmind.png'
      }
    };

  const trendingTopics = [
    { name: "CBD Research", count: 156, growth: "+23%", emoji: "🧬", color: "#3b82f6", hot: true },
    { name: "Indoor Growing", count: 134, growth: "+18%", emoji: "🏠", color: "#10b981", trending: true },
    { name: "Terpene Studies", count: 98, growth: "+15%", emoji: "🔬", color: "#8b5cf6", new: true },
    { name: "Pain Management", count: 87, growth: "+12%", emoji: "💊", color: "#ef4444", verified: true }
  ];

  const achievements = [
    { name: "First Read", icon: "📖", earned: true },
    { name: "Researcher", icon: "🔬", earned: true },
    { name: "Expert Reader", icon: "🎓", earned: true },
    { name: "Knowledge Guru", icon: "🧠", earned: false },
    { name: "Science Master", icon: "👨‍🔬", earned: false }
  ];

  const getCategoryTheme = (category) => {
    const themes = {
      medical: { 
        icon: <Microscope size={32} />, 
        gradient: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        bgPattern: '#1e40af15'
      },
      growing: { 
        icon: <Leaf size={32} />, 
        gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        bgPattern: '#05966915'
      },
      strains: { 
        icon: <Beaker size={32} />, 
        gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        bgPattern: '#7c3aed15'
      },
      general: { 
        icon: <BookOpen size={32} />, 
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        bgPattern: '#d9770615'
      }
    };
    return themes[category] || themes.general;
  };

  const handleVote = async (articleId, voteType) => {
    try {
      const response = await researchAPI.voteArticle(articleId, voteType);
      
      setArticles(prevArticles => 
        prevArticles.map(article => 
          article.id === articleId 
            ? { ...article, like_count: response.like_count }
            : article
        )
      );

      setVotedArticles(prev => {
        const newMap = new Map(prev);
        if (response.vote_type) {
          newMap.set(articleId, response.vote_type);
        } else {
          newMap.delete(articleId);
        }
        return newMap;
      });

    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const getVoteScore = (article) => {
    return article.like_count || 0;
  };
  const handleBookmark = (articleId) => {
    setSavedArticles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(articleId)) {
        newSet.delete(articleId);
      } else {
        newSet.add(articleId);
      }
      return newSet;
    });
  };
  

  const filteredArticles = articles.filter(article => {
    const matchesCategory = activeCategory === 'all' || article.category === activeCategory;
    const matchesTag = !selectedTag || article.tags.includes(selectedTag);
    const matchesSearch = !searchTerm || 
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesTag && matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'hot': 
        return (b.engagement + (b.isHot ? 20 : 0) + (b.isTrending ? 15 : 0)) - 
               (a.engagement + (a.isHot ? 20 : 0) + (a.isTrending ? 15 : 0));
      case 'new': 
        return new Date(b.publishedDate) - new Date(a.publishedDate);
      case 'top': 
        return b.likes - a.likes;
      case 'trending':
        return (b.isTrending ? 1 : 0) - (a.isTrending ? 1 : 0);
      default: 
        return 0;
    }
  });

  console.log('I BEER DRINK BEER',filteredArticles)

  const generateCategoryPattern = (category) => {
    const patterns = {
      medical: (
        <svg width="100%" height="100%" style={{ position: 'absolute', opacity: 0.1 }}>
          <defs>
            <pattern id={`medical-pattern-${category}`} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="2" fill="white" />
              <circle cx="0" cy="0" r="1" fill="white" />
              <circle cx="40" cy="40" r="1" fill="white" />
              <circle cx="0" cy="40" r="1" fill="white" />
              <circle cx="40" cy="0" r="1" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#medical-pattern-${category})`} />
        </svg>
      ),
      growing: (
        <svg width="100%" height="100%" style={{ position: 'absolute', opacity: 0.1 }}>
          <defs>
            <pattern id={`growing-pattern-${category}`} x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
              <path d="M15,5 Q20,10 15,15 Q10,10 15,5" fill="white" />
              <path d="M5,20 Q10,25 15,20" fill="white" />
              <path d="M20,25 Q25,20 30,25" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#growing-pattern-${category})`} />
        </svg>
      ),
      strains: (
        <svg width="100%" height="100%" style={{ position: 'absolute', opacity: 0.1 }}>
          <defs>
            <pattern id={`strains-pattern-${category}`} x="0" y="0" width="25" height="25" patternUnits="userSpaceOnUse">
              <polygon points="12.5,2 20,10 12.5,18 5,10" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#strains-pattern-${category})`} />
        </svg>
      ),
      general: (
        <svg width="100%" height="100%" style={{ position: 'absolute', opacity: 0.1 }}>
          <defs>
            <pattern id={`general-pattern-${category}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <rect x="8" y="8" width="4" height="4" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#general-pattern-${category})`} />
        </svg>
      )
    };
    return patterns[category] || patterns.general;
  };

  const FallbackThumbnail = ({ article }) => {
    const theme = getCategoryTheme(article.category);
    
    return (
      <div style={{
        height: '180px',
        background: theme.gradient,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        {generateCategoryPattern(article.category)}
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          zIndex: 1,
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}>
            {theme.icon}
          </div>
          
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: 'rgba(255, 255, 255, 0.9)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '6px 12px',
            borderRadius: '8px',
            backdropFilter: 'blur(5px)'
          }}>
            {article.category} Research
          </div>
          
          <div style={{
            fontSize: '11px',
            color: 'rgba(255, 255, 255, 0.7)',
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '4px 8px',
            borderRadius: '6px',
            textTransform: 'capitalize'
          }}>
            {article.type}
          </div>
        </div>
        
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '60px',
          height: '60px',
          background: 'rgba(255, 255, 255, 0.05)',
          clipPath: 'polygon(100% 0, 0 0, 100% 100%)'
        }} />
      </div>
    );
  };

  const handleOpenArticle = async (article) => {
    setSelectedArticle(article);
    setShowArticleDetail(true);
    
    try {
      const viewData = await researchAPI.trackArticleView(article.id);
      if (viewData && viewData.success) {
        setArticles(prevArticles => 
          prevArticles.map(a => 
            a.id === article.id 
              ? { ...a, view_count: viewData.view_count }
              : a
          )
        );
        
        if (featuredStudy && featuredStudy.id === article.id) {
          setFeaturedStudy(prev => ({ ...prev, view_count: viewData.view_count }));
        }
      }
    } catch (error) {
      console.warn('Failed to track article view:', error);
    }
  };

  const FeaturedStudyCard = () => {
    if (!featuredStudy) {
      return (
        <div style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          borderRadius: isMobile ? '16px' : '24px',
          padding: isMobile ? '20px' : '32px',
          color: 'white',
          marginBottom: isMobile ? '20px' : '32px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(59, 130, 246, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: isMobile ? '150px' : '200px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(255, 255, 255, 0.3)',
              borderTop: '3px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }} />
            <p style={{ margin: 0, opacity: 0.8 }}>Loading featured research...</p>
          </div>
        </div>
      );
    }

    return (
      <div style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        borderRadius: isMobile ? '16px' : '24px',
        padding: isMobile ? '20px' : '32px',
        color: 'white',
        marginBottom: isMobile ? '20px' : '32px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(59, 130, 246, 0.3)'
      }}>
        {featuredStudy.thumbnail && !isMobile && (
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: '40%',
            backgroundImage: `url(${featuredStudy.thumbnail})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.3,
            borderRadius: '0 24px 24px 0'
          }} />
        )}
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              <Microscope size={14} />
              Featured Research
            </div>
            {featuredStudy.exclusiveContent && (
              <div style={{
                padding: '4px 8px',
                background: '#ef4444',
                borderRadius: '8px',
                fontSize: '11px',
                fontWeight: '600',
                animation: 'pulse 2s infinite'
              }}>
                EXCLUSIVE
              </div>
            )}
          </div>
          
          <h2 style={{
            margin: '0 0 12px 0',
            fontSize: isMobile ? '20px' : '28px',
            fontWeight: '700',
            lineHeight: '1.2',
            maxWidth: isMobile ? '100%' : '60%'
          }}>
            {featuredStudy.title}
          </h2>
          
          <p style={{
            margin: '0 0 20px 0',
            fontSize: isMobile ? '14px' : '16px',
            opacity: 0.9,
            lineHeight: '1.5',
            maxWidth: isMobile ? '100%' : '60%'
          }}>
            {featuredStudy.summary || featuredStudy.description}
          </p>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '12px' : '24px',
            marginBottom: '24px',
            fontSize: '14px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: 0.8 }}>
              <Globe size={14} />
              <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{featuredStudy.institution}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: 0.8 }}>
              <Calendar size={14} />
              <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{featuredStudy.publishedDate || featuredStudy.published_date}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: 0.8 }}>
              <Eye size={14} />
              <span style={{ fontSize: isMobile ? '12px' : '14px' }}>
                {(featuredStudy.views || featuredStudy.view_count || 0).toLocaleString()} views
              </span>
            </div>
            {featuredStudy.citations && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: 0.8 }}>
                <BookOpen size={14} />
                <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{featuredStudy.citations} citations</span>
              </div>
            )}
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <button 
              onClick={() => handleOpenArticle(featuredStudy)}
              style={{
                padding: isMobile ? '12px 20px' : '14px 28px',
                background: 'white',
                color: '#1e40af',
                border: 'none',
                borderRadius: '12px',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                flex: isMobile ? '1' : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Read Full Study
              <ArrowRight size={16} />
            </button>
            
            <button style={{
              padding: isMobile ? '12px 16px' : '14px 20px',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              flex: isMobile ? 'none' : 'none'
            }}>
              <Bookmark size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Mobile Filter Modal
  const MobileFilterModal = () => {
    if (!showMobileFilters) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center'
      }}>
        <div style={{
          background: colors.bgTertiary,
          borderRadius: '20px 20px 0 0',
          padding: '24px',
          width: '100%',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{
              margin: 0,
              color: '#e5e5e5',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              Filters & Search
            </h3>
            <button
              onClick={() => setShowMobileFilters(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                padding: '8px'
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Search */}
          <div style={{ marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="Search research papers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '16px',
                border: '2px solid #333',
                borderRadius: '12px',
                fontSize: '16px',
                background: colors.bgCard,
                color: '#e5e5e5',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Sort By */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e5e5e5', fontWeight: '500' }}>
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                width: '100%',
                padding: '16px',
                border: '2px solid #333',
                borderRadius: '12px',
                fontSize: '16px',
                background: colors.bgCard,
                color: '#e5e5e5',
                outline: 'none'
              }}
            >
              <option value="hot">Hot Research</option>
              <option value="new">Latest Studies</option>
              <option value="top">Most Cited</option>
              <option value="trending">Trending Now</option>
            </select>
          </div>

          {/* Categories */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '12px', color: '#e5e5e5', fontWeight: '500' }}>
              Categories
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '8px'
            }}>
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  style={{
                    padding: '12px',
                    background: activeCategory === category.id 
                      ? `linear-gradient(135deg, ${category.color} 0%, ${category.color}dd 100%)`
                      : '#2a2a2a',
                    color: activeCategory === category.id ? 'white' : '#e5e5e5',
                    border: '1px solid',
                    borderColor: activeCategory === category.id ? category.color : '#333',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    textAlign: 'center'
                  }}
                >
                  {category.icon}
                  <span style={{ fontSize: '12px' }}>{category.name}</span>
                  <span style={{ fontSize: '10px', opacity: 0.8 }}>{category.count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '12px', color: '#e5e5e5', fontWeight: '500' }}>
              Research Topics
            </label>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              {tags.slice(0, 15).map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                  style={{
                    padding: '8px 12px',
                    background: selectedTag === tag 
                      ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                      : 'transparent',
                    color: selectedTag === tag ? 'white' : '#888',
                    border: '1px solid',
                    borderColor: selectedTag === tag ? '#00d4aa' : '#333',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => setShowMobileFilters(false)}
            style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Apply Filters
          </button>
        </div>
      </div>
    );
  };

  // Category Filter
  const CategoryFilter = () => (
    <div style={{
      display: 'flex',
      gap: '12px',
      marginBottom: '24px',
      overflowX: 'auto',
      paddingBottom: '4px'
    }}>
      {categories.map(category => (
        <button
          key={category.id}
          onClick={() => setActiveCategory(category.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            background: activeCategory === category.id 
              ? `linear-gradient(135deg, ${category.color} 0%, ${category.color}dd 100%)`
              : colors.bgCard,
            color: activeCategory === category.id ? 'white' : '#e5e5e5',
            border: '1px solid',
            borderColor: activeCategory === category.id ? category.color : '#333',
            borderRadius: '16px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap',
            position: 'relative'
          }}
          onMouseEnter={(e) => {
            if (activeCategory !== category.id) {
              e.currentTarget.style.borderColor = category.color;
              e.currentTarget.style.color = category.color;
            }
          }}
          onMouseLeave={(e) => {
            if (activeCategory !== category.id) {
              e.currentTarget.style.borderColor = '#333';
              e.currentTarget.style.color = '#e5e5e5';
            }
          }}
        >
          {category.icon}
          {category.name}
          <span style={{
            padding: '2px 6px',
            background: activeCategory === category.id 
              ? 'rgba(255, 255, 255, 0.2)'
              : 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            fontSize: '11px',
            fontWeight: '600'
          }}>
            {category.count}
          </span>
          
          {category.trending && (
            <div style={{
              position: 'absolute',
              top: '-6px',
              right: '-6px',
              width: '12px',
              height: '12px',
              background: '#ef4444',
              borderRadius: '50%',
              animation: 'pulse 2s infinite'
            }} />
          )}
          {category.new > 0 && (
            <span style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              padding: '2px 6px',
              background: '#10b981',
              color: 'white',
              borderRadius: '10px',
              fontSize: '10px',
              fontWeight: '600'
            }}>
              {category.new}
            </span>
          )}
        </button>
      ))}
    </div>
  );

const ArticleCard = ({ article }) => (
  <div style={{
    background: colors.comeCard,
    borderRadius: '20px',
    overflow: 'hidden',
    border: '1px solid #333',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    position: 'relative',
    width: isMobile ? '95%' : 'auto',
    margin: isMobile ? '0 auto' : '0'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.borderColor = '#00d4aa';
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 212, 170, 0.2)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.borderColor = '#333';
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
  }}
  onClick={() => handleOpenArticle(article)}
  >    

    {/* Thumbnail with overlays */}
    <div style={{
      height: isMobile ? '140px' : '180px',
      position: 'relative',
      ...(article.thumbnail ? {
        backgroundImage: `url(${article.thumbnail})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : {})
    }}>
      {!article.thumbnail && <FallbackThumbnail article={article} />}
      <div style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap'
      }}>
        <span style={{
          padding: '4px 8px',
          background: article.type === 'research' ? '#3b82f6' : '#10b981',
          color: 'white',
          borderRadius: '8px',
          fontSize: isMobile ? '10px' : '11px',
          fontWeight: '600',
          textTransform: 'capitalize'
        }}>
          {article.type}
        </span>
        
        {article.isPeerReviewed && (
          <span style={{
            padding: '4px 8px',
            background: '#fbbf24',
            color: 'white',
            borderRadius: '8px',
            fontSize: isMobile ? '10px' : '11px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Award size={8} />
            Peer Reviewed
          </span>
        )}
        
        {article.isHot && (
          <span style={{
            padding: '4px 8px',
            background: '#ef4444',
            color: 'white',
            borderRadius: '8px',
            fontSize: isMobile ? '10px' : '11px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            animation: 'pulse 2s infinite'
          }}>
            <Flame size={8} />
            HOT
          </span>
        )}
        
        {article.isTrending && (
          <span style={{
            padding: '4px 8px',
            background: '#8b5cf6',
            color: 'white',
            borderRadius: '8px',
            fontSize: isMobile ? '10px' : '11px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <TrendingUp size={8} />
            Trending
          </span>
        )}
        
        {article.isNew && (
          <span style={{
            padding: '4px 8px',
            background: '#10b981',
            color: 'white',
            borderRadius: '8px',
            fontSize: isMobile ? '10px' : '11px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Sparkles size={8} />
            NEW
          </span>
        )}
        
        {article.hasVideo && (
          <div style={{
            padding: '4px 8px',
            background: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '8px',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Play size={8} />
            Video
          </div>
        )}
      </div>
      
      <div style={{
        position: 'absolute',
        top: '12px',
        right: '12px',
        display: 'flex',
        gap: '8px',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <span style={{
          padding: '4px 8px',
          background: `rgba(0, 0, 0, 0.7)`,
          color: 'white',
          borderRadius: '8px',
          fontSize: isMobile ? '10px' : '11px',
          fontWeight: '600'
        }}>
          {article.difficulty}
        </span>
        
        {article.credibilityRating && (
          <div style={{
            padding: '4px 8px',
            background: article.credibilityRating === 'A+' ? '#10b981' : '#fbbf24',
            color: 'white',
            borderRadius: '8px',
            fontSize: isMobile ? '10px' : '11px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Star size={8} />
            {article.credibilityRating}
          </div>
        )}
      </div>
      
      {/* Engagement score overlay */}
      <div style={{
        position: 'absolute',
        bottom: '12px',
        right: '12px',
        padding: '6px 10px',
        background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
        borderRadius: '12px',
        color: 'white',
        fontSize: isMobile ? '11px' : '12px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <Zap size={10} />
        {article.engagement}%
      </div>
    </div>

    {/* Content */}
    <div style={{ padding: isMobile ? '16px' : '20px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
        flexWrap: 'wrap'
      }}>
        <span style={{
          padding: '2px 8px',
          background: '#3b82f620',
          color: '#3b82f6',
          borderRadius: '8px',
          fontSize: isMobile ? '10px' : '11px',
          fontWeight: '600'
        }}>
          {article.category.charAt(0).toUpperCase() + article.category.slice(1)}
        </span>
        
        <span style={{ fontSize: '12px', color: '#666' }}>•</span>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Clock size={10} style={{ color: '#888' }} />
          <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#888' }}>
            {article.read_time} min read
          </span>
        </div>

        {article.tags.slice(0, isMobile ? 2 : 4).map(tag => (
          <span 
            key={tag} 
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTag(selectedTag === tag ? null : tag);
            }}
            style={{
              padding: '4px 8px',
              background: selectedTag === tag ? '#00d4aa' : '#333',
              color: selectedTag === tag ? 'white' : colors.textSecondary1,
              borderRadius: '8px',
              fontSize: isMobile ? '10px' : '11px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              if (selectedTag !== tag) {
                e.currentTarget.style.background = '#555';
                e.currentTarget.style.color = '#e5e5e5';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedTag !== tag) {
                e.currentTarget.style.background = '#333';
                e.currentTarget.style.color = '#888';
              }
            }}
          >
            #{tag}
          </span>
        ))}
        {article.tags.length > (isMobile ? 2 : 4) && (
          <span style={{
            padding: '4px 8px',
            background: '#333',
            color: '#888',
            borderRadius: '8px',
            fontSize: isMobile ? '10px' : '11px'
          }}>
            +{article.tags.length - (isMobile ? 2 : 4)} more
          </span>
        )}
      
        
        {article.impactScore && (
          <>
            <span style={{ fontSize: '12px', color: '#666' }}>•</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Target size={10} style={{ color: '#fbbf24' }} />
              <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#fbbf24', fontWeight: '600' }}>
                Impact: {article.impactScore}
              </span>
            </div>
          </>
        )}
      </div>
      
      <h3 style={{
        margin: '0 0 8px 0',
        fontSize: isMobile ? '16px' : '18px',
        fontWeight: '600',
        color: '#e5e5e5',
        lineHeight: '1.3'
      }}>
        {article.title}
      </h3>
      
      <p style={{
        margin: '0 0 12px 0',
        fontSize: isMobile ? '13px' : '14px',
        color: colors.accentSecondary,
        fontWeight: '500'
      }}>
        {article.subtitle}
      </p>
      
      <p style={{
        margin: '0 0 16px 0',
        fontSize: isMobile ? '13px' : '14px',
        color: '#bbb',
        lineHeight: '1.5',
        display: '-webkit-box',
        WebkitLineClamp: isMobile ? 2 : 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden'
      }}>
        {article.excerpt}
      </p>
      
      {/* Stats section */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '12px' : '16px',
        marginBottom: '16px',
        flexWrap: 'wrap'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '4px', 
          color: '#888',
          transition: 'color 0.3s ease' 
        }}>
          <Eye size={12} />
          <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: '500' }}>
            {(article?.views || article?.view_count || 0).toLocaleString()}
          </span>
        </div>
        {article.sources && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px', 
            color: '#888',
            transition: 'color 0.3s ease' 
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#e5e5e5'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#888'}
          >
            <BookOpen size={12} />
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: '500' }}>
              {article.sources} sources
            </span>
          </div>
        )}
        
        {article.downloadCount && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px', 
            color: '#888',
            transition: 'color 0.3s ease' 
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#e5e5e5'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#888'}
          >
            <Download size={12} />
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: '500' }}>
              {article.downloadCount}
            </span>
          </div>
        )}
      </div>

      {/* Mobile layout: Stack voting and author sections */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: isMobile ? '16px' : '20px',
        marginBottom: '16px',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        
        {/* Voting section */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'flex-start',
          gap: isMobile ? '16px' : '8px',
          minWidth: isMobile ? 'auto' : '70px',
          width: isMobile ? '90%' : 'auto',
          background: colors.bgGradient1,
          padding: isMobile ? '12px 16px' : '16px 12px',
          borderRadius: '16px',
          border: '1px solid #333'
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleVote(article.id, votedArticles.get(article.id) === 'up' ? null : 'up');
            }}
            style={{
              padding: '8px',
              borderRadius: '12px',
              border: 'none',
              background: votedArticles.get(article.id) === 'up'
                ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                : 'white',
              color: votedArticles.get(article.id) === 'up' ? 'white' : '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: votedArticles.get(article.id) === 'up' ? 'scale(1.1)' : 'scale(1)',
              boxShadow: votedArticles.get(article.id) === 'up' 
                ? '0 4px 12px rgba(16, 185, 129, 0.4)' 
                : '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              if (votedArticles.get(article.id) !== 'up') {
                e.currentTarget.style.background = 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)';
                e.currentTarget.style.color = '#00d4aa';
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (votedArticles.get(article.id) !== 'up') {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#6b7280';
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}
          >
            <ChevronUp size={isMobile ? 16 : 20} />
          </button>
          
          <span style={{
            fontWeight: 'bold',
            fontSize: isMobile ? '16px' : '20px',
            color: votedArticles.get(article.id) === 'up' ? '#00d4aa' : 
                  votedArticles.get(article.id) === 'down' ? '#ef4444' : '#374151',
            transition: 'all 0.3s ease'
          }}>
            {getVoteScore(article)}
          </span>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleVote(article.id, votedArticles.get(article.id) === 'down' ? null : 'down');
            }}
            style={{
              padding: '8px',
              borderRadius: '12px',
              border: 'none',
              background: votedArticles.get(article.id) === 'down'
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : 'white',
              color: votedArticles.get(article.id) === 'down' ? 'white' : '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: votedArticles.get(article.id) === 'down' ? 'scale(1.1)' : 'scale(1)',
              boxShadow: votedArticles.get(article.id) === 'down' 
                ? '0 4px 12px rgba(239, 68, 68, 0.4)' 
                : '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              if (votedArticles.get(article.id) !== 'down') {
                e.currentTarget.style.background = 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)';
                e.currentTarget.style.color = '#ef4444';
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (votedArticles.get(article.id) !== 'down') {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#6b7280';
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}
          >
            <ChevronDown size={isMobile ? 16 : 20} />
          </button>
        </div>

        {/* Author and submitter section */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', width: isMobile ? '100%' : 'auto' }}>
          
          {/* Author section */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px',
            background: colors.bgCard,
            borderRadius: '12px',
            border: '1px solid #333'
          }}>
            <div style={{
              width: isMobile ? '28px' : '32px',
              height: isMobile ? '28px' : '32px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '600'
            }}>
              {article.author.charAt(0)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#e5e5e5', fontWeight: '500' }}>
                {article.author}
              </div>
              <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#888' }}>
                {article.institution}
              </div>
            </div>
            <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#888' }}>
              {article.publishedDate}
            </div>
          </div>

          {/* Submitter section */}
          {article.submitter && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 0 0 0',
              borderTop: '1px solid #333'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: isMobile ? '11px' : '12px',
                color: '#888'
              }}>
                <span style={{ marginRight: isMobile ? '8px' : '16px' }}></span>  
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '8px' : '12px'
                }}>    
                  <img
                    src={
                      article.submitter.profile_image_url || 
                      (article.submitter.smoker_level === 'stoner' ? `/peng1.png` : 
                      article.submitter.smoker_level === 'beginner' ? `/chick1.png` : 
                      article.submitter.smoker_level === 'connoisseur' ? `/dog.png` : 
                        `/pengu3.png`)
                    }
                    alt={article.submitter.username}
                    style={{
                      width: isMobile ? '32px' : '40px',
                      height: isMobile ? '32px' : '40px',
                      borderRadius: '12px',
                      objectFit: 'cover'
                    }}
                  />
                          
                  {/* Smoker Level Badge */}
                  <div style={{
                    position: isMobile ? 'static' : 'absolute',
                    bottom: isMobile ? 'auto' : '0%',
                    right: isMobile ? 'auto' : '105%',
                    backgroundColor: colors.imgBackground2,
                    borderRadius: '50%',
                    width: isMobile ? '32px' : '40px',
                    height: isMobile ? '32px' : '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    border: '1px solid #00d4aa'
                  }}>
                    <img 
                      src={getSmokerEmoji(article.submitter.smoker_level)}
                      alt={`${article.submitter.smoker_level} level`}
                      style={{
                        width: isMobile ? '28px' : '36px',
                        height: isMobile ? '28px' : '36px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                </div>

                {/* Submitter details */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '4px'
                  }}>
                  <span 
                    className="clickable-username"
                    style={{
                      fontSize: isMobile ? '12px' : '13px',
                      fontWeight: '600',
                      color: '#e5e5e5',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    onClick={(e) => {
                          e.stopPropagation();
                          handleUsernameClick(article?.submitter?.id);
                        }}
                  >
                      Submitted by {article.submitter.handle || article.submitter.username}
                    </span>
                    <div style={{
                      padding: '2px 6px',
                      background: '#00d4aa20',
                      color: colors.accentPrimary1,
                      borderRadius: '6px',
                      fontSize: isMobile ? '9px' : '10px',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {article.submitter.smoker_level}
                    </div>
                  </div>
                  
                  <div style={{
                    fontSize: isMobile ? '11px' : '12px',
                    color: '#888'
                  }}>
                    {article.submitted_at} • Member since {article.submitter.created_at}
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBookmark(article.id);
                  }}
                  style={{
                    padding: isMobile ? '6px' : '8px',
                    background: savedArticles.has(article.id) 
                      ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' 
                      : 'transparent',
                    border: '1px solid',
                    borderColor: savedArticles.has(article.id) ? '#fbbf24' : '#333',
                    borderRadius: '8px',
                    color: savedArticles.has(article.id) ? 'white' : '#888',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!savedArticles.has(article.id)) {
                      e.currentTarget.style.borderColor = '#fbbf24';
                      e.currentTarget.style.color = '#fbbf24';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!savedArticles.has(article.id)) {
                      e.currentTarget.style.borderColor = '#333';
                      e.currentTarget.style.color = '#888';
                    }
                  }}
                >
                  <Bookmark size={isMobile ? 10 : 12} />
                </button>
                
                <button style={{
                  padding: isMobile ? '6px' : '8px',
                  background: 'transparent',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: '#888',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#8b5cf6';
                  e.currentTarget.style.color = '#8b5cf6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#333';
                  e.currentTarget.style.color = '#888';
                }}
                >
                  <Share2 size={isMobile ? 10 : 12} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Call to action */}
      <button style={{
        width: '100%',
        padding: isMobile ? '10px' : '12px',
        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: isMobile ? '13px' : '14px',
        fontWeight: '600',
        marginTop: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 8px 20px rgba(139, 92, 246, 0.4)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
      >
        Read Full Study
        <ArrowRight size={isMobile ? 12 : 14} />
      </button>
    </div>
  </div>
);

  // Tag Filter Component
  const TagFilter = () => (
    <div style={{
      marginBottom: '24px',
      padding: '20px',
      background: colors.comeCard,
      borderRadius: '16px',
      border: '1px solid #333'
    }}>
      <h3 style={{
        margin: '0 0 12px 0',
        fontSize: '14px',
        fontWeight: '600',
        color: '#e5e5e5',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Tag size={14} />
        Research Topics
        {selectedTag && (
          <button
            onClick={() => setSelectedTag(null)}
            style={{
              padding: '2px 6px',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '10px',
              cursor: 'pointer',
              marginLeft: '8px'
            }}
          >
            Clear
          </button>
        )}
      </h3>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        {tags.map(tag => (
          <button
            key={tag}
            onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
            style={{
              padding: '6px 12px',
              background: selectedTag === tag 
                ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                : 'transparent',
              color: selectedTag === tag ? 'white' : '#888',
              border: '1px solid',
              borderColor: selectedTag === tag ? '#00d4aa' : '#333',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              if (selectedTag !== tag) {
                e.currentTarget.style.borderColor = '#00d4aa';
                e.currentTarget.style.color = '#00d4aa';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedTag !== tag) {
                e.currentTarget.style.borderColor = '#333';
                e.currentTarget.style.color = '#888';
              }
            }}
          >
            {tag}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0a0a',
      color: 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .slide-in {
          animation: slideIn 0.5s ease-out;
        }
      `}</style>

      <div style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: isMobile ? '16px' : '24px' 
      }}>
        {/* Enhanced Header */}
        <div style={{
          background: colors.bgTertiary,
          borderRadius: isMobile ? '16px' : '24px',
          padding: isMobile ? '20px' : '32px',
          border: '1px solid #333',
          marginBottom: isMobile ? '20px' : '32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '20px' : '0'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '20px',
              textAlign: isMobile ? 'center' : 'left',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <div style={{
                width: isMobile ? '60px' : '80px',
                height: isMobile ? '60px' : '80px',
                background: 'linear-gradient(135deg, #10b981 0%, #00d4aa 100%)',
                borderRadius: isMobile ? '20px' : '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4)'
              }}>
                <Microscope size={isMobile ? 32 : 40} style={{ color: 'white' }} />
              </div>
              <div>
                <h1 style={{
                  margin: '0 0 8px 0',
                  fontSize: isMobile ? '28px' : '36px',
                  fontWeight: '800',
                  background: 'linear-gradient(135deg, #10b981 0%, #00d4aa 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Cannabis Research Hub
                </h1>
                <p style={{
                  margin: 0,
                  fontSize: isMobile ? '16px' : '18px',
                  color: '#888'
                }}>
                  Scientific studies, growing guides, and evidence-based knowledge
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  marginTop: '8px',
                  flexWrap: 'wrap',
                  justifyContent: isMobile ? 'center' : 'flex-start'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#10b981' }}>
                    <Users size={14} />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                      {headerStats.members} members
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#8b5cf6' }}>
                    <BookOpen size={14} />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                      {headerStats.studies} studies
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#f59e0b' }}>
                    <Award size={14} />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                      {headerStats.contributions} contributions
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              flexDirection: isMobile ? 'row' : 'row',
              width: isMobile ? '100%' : 'auto'
            }}>
              <div style={{ position: 'relative' }}>
                <button style={{
                  padding: '12px',
                  background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
                  border: '1px solid #333',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)';
                  e.currentTarget.style.borderColor = '#f59e0b';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)';
                  e.currentTarget.style.borderColor = '#333';
                }}
                >
                  <Bell size={20} style={{ color: '#888' }} />
                  {notifications > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      width: '20px',
                      height: '20px',
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      color: 'white',
                      fontWeight: '600',
                      border: '2px solid #0a0a0a'
                    }}>
                      {notifications}
                    </div>
                  )}
                </button>
              </div>
              
              <button style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: isMobile ? '12px 16px' : '16px 24px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                flex: isMobile ? 1 : 'none'
              }}
              onClick={() => setShowSubmitDialog(true)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
              }}
              >
                <Plus size={isMobile ? 16 : 20} />
                {isMobile ? 'Submit' : 'Submit Research'}
              </button>
            </div>
          </div>
        </div>

        {/* Featured Study */}
        <FeaturedStudyCard />

        {/* Search and Filters */}
        <div style={{
          background: colors.comeCard,
          borderRadius: '20px',
          padding: isMobile ? '16px' : '24px',
          border: '1px solid #333',
          marginBottom: '24px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            marginBottom: '20px',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={20} style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#888'
              }} />
              <input
                type="text"
                placeholder="Search research papers, studies, and scientific articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  paddingLeft: '48px',
                  paddingRight: '16px',
                  paddingTop: '16px',
                  paddingBottom: '16px',
                  border: '2px solid #333',
                  borderRadius: '16px',
                  fontSize: '16px',
                  background: colors.bgCard,
                  color: '#e5e5e5',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.boxShadow = '0 0 0 4px rgba(16, 185, 129, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#333';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              width: isMobile ? '100%' : 'auto'
            }}>
              {isMobile ? (
                <button
                  onClick={() => setShowMobileFilters(true)}
                  style={{
                    flex: 1,
                    padding: '16px 20px',
                    border: '2px solid #333',
                    borderRadius: '16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    outline: 'none',
                    cursor: 'pointer',
                    background: colors.bgCard,
                    color: '#e5e5e5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Filter size={16} />
                  Filters & Sort
                </button>
              ) : (
                <>
                  <Filter size={16} style={{ color: '#888' }} />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{
                      padding: '16px 20px',
                      border: '2px solid #333',
                      borderRadius: '16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      outline: 'none',
                      cursor: 'pointer',
                      background: colors.bgCard,
                      color: '#e5e5e5'
                    }}
                  >
                    <option value="hot">Hot Research</option>
                    <option value="new">Latest Studies</option>
                    <option value="top">Most Cited</option>
                    <option value="trending">Trending Now</option>
                  </select>
                </>
              )}
            </div>
          </div>
          
          {!isMobile && <CategoryFilter />}
          
          {/* Quick Access Pills - Hidden on mobile */}
          {!isMobile && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '16px' }}>
              {['Peer Reviewed Only', 'Video Content', 'Free Download', 'Recent Studies', 'High Impact'].map((filter, index) => (
                <button
                  key={filter}
                  style={{
                    padding: '8px 16px',
                    background: index === 0 ? 'linear-gradient(135deg, #10b981 0%, #00d4aa 100%)' : 'transparent',
                    color: index === 0 ? 'white' : '#888',
                    border: '1px solid',
                    borderColor: index === 0 ? '#10b981' : '#333',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (index !== 0) {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)';
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.color = '#047857';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (index !== 0) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = '#333';
                      e.currentTarget.style.color = '#888';
                    }
                  }}
                >
                  {filter}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 3fr 1fr', 
          gap: isMobile ? '24px' : '32px' 
        }}>
          {/* Left Sidebar - Hidden on mobile */}
          {!isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <UserLevelCard stats={userStats} isAuthenticated={isAuthenticated} />
              <AchievementsCard 
                profileData={profileData} 
                loading={achievementsLoading} 
                isAuthenticated={isAuthenticated} 
              />
              <TagFilter />
            </div>
          )}

          {/* Main Content */}
          <div style={{ order: isMobile ? 1 : 0 }}>
            {/* Research Insights Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              borderRadius: '16px',
              padding: isMobile ? '16px' : '20px',
              marginBottom: '24px',
              color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Zap size={20} />
                <h3 style={{ margin: 0, fontSize: isMobile ? '14px' : '16px', fontWeight: '600' }}>
                  Weekly Research Insights
                </h3>
              </div>
              <p style={{ margin: '0 0 12px 0', fontSize: isMobile ? '13px' : '14px', opacity: 0.9 }}>
                 {headerStats.studies} new studies published • {headerStats.total_growing} grow related papers • {headerStats.total_medical} medical discoveries
              </p>
              <button style={{
                padding: '8px 16px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer'
              }}>
                View Full Report →
              </button>
            </div>

            {/* Mobile Stats Cards - Show above articles on mobile */}
            {isMobile && (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '16px', 
                marginBottom: '24px' 
              }}>
                <UserLevelCard stats={userStats} isAuthenticated={isAuthenticated} />
                <MobileStatsToggle 
                  profileData={profileData}
                  achievementsLoading={achievementsLoading}
                  isAuthenticated={isAuthenticated}
                  trendingTopics={trendingTopics}
                  colors={colors}
                />
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              padding: '16px 24px',
              background: colors.comeCard,
              borderRadius: '12px',
              border: '1px solid #333',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '16px' : '0'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                width: isMobile ? '100%' : 'auto',
                justifyContent: isMobile ? 'space-between' : 'flex-start'
              }}>
                <span style={{ fontSize: '14px', color: '#888' }}>Articles per page:</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[10, 25, 50].map((count) => (
                    <button
                      key={count}
                      onClick={() => setArticlesPerPage(count)}
                      style={{
                        padding: isMobile ? '6px 10px' : '8px 12px',
                        background: articlesPerPage === count 
                          ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)' 
                          : colors.comeCard1,
                        color: articlesPerPage === count ? 'white' : '#888',
                        border: '1px solid',
                        borderColor: articlesPerPage === count ? '#00d4aa' : '#333',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>
              
              <div style={{ 
                fontSize: '14px', 
                color: '#888',
                textAlign: isMobile ? 'center' : 'right'
              }}>
                Showing {((currentPage - 1) * articlesPerPage) + 1}-{Math.min(currentPage * articlesPerPage, totalArticles)} of {totalArticles} articles
              </div>
            </div>

            {/* Articles Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: isMobile ? '20px' : '24px',
              marginBottom: '32px'
            }}>
              {filteredArticles.map((article, index) => (
                <div key={article.id} className="slide-in" style={{
                  animationDelay: `${index * 0.1}s`
                }}>
                  <ArticleCard article={article} />
                </div>
              ))}
            </div>
            
            {filteredArticles.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: isMobile ? '48px 16px' : '64px 24px',
                background: colors.bgTertiary,
                borderRadius: '20px',
                border: '1px solid #333'
              }}>
                <Microscope size={isMobile ? 40 : 48} style={{ color: '#666', marginBottom: '16px' }} />
                <h3 style={{ margin: '0 0 8px 0', color: '#e5e5e5', fontSize: isMobile ? '18px' : '20px', fontWeight: '600' }}>
                  No research found
                </h3>
                <p style={{ margin: '0 0 24px 0', color: '#888', fontSize: isMobile ? '14px' : '16px' }}>
                  Try adjusting your filters or search terms to discover relevant studies
                </p>
                <button style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #10b981 0%, #00d4aa 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  Clear All Filters
                </button>
              </div>
            )}

            {/* Load More with Engagement Hook */}
            {filteredArticles.length > 0 && (
              <div style={{
                textAlign: 'center',
                padding: isMobile ? '32px 16px' : '40px',
                background: colors.bgTertiary,
                borderRadius: '20px',
                border: '1px solid #333',
                marginTop: '32px'
              }}>
                {hasNextPage ? (
                  <div>
                    <div style={{ marginBottom: '20px' }}>
                      <span style={{ fontSize: isMobile ? '40px' : '48px', marginBottom: '12px', display: 'block' }}>🚀</span>
                      <h3 style={{
                        margin: '0 0 8px 0',
                        fontSize: isMobile ? '20px' : '24px',
                        fontWeight: '700',
                        color: '#e5e5e5'
                      }}>
                        More Research Awaits
                      </h3>
                      <p style={{
                        margin: 0,
                        color: '#888',
                        fontSize: isMobile ? '14px' : '16px',
                        lineHeight: '1.5'
                      }}>
                        Page {currentPage} of {totalPages} • {totalArticles} total articles
                      </p>
                    </div>

                    {/* Progress bar */}
                    <div style={{
                      width: '100%',
                      height: '4px',
                      background: '#333',
                      borderRadius: '2px',
                      overflow: 'hidden',
                      marginBottom: '24px'
                    }}>
                      <div style={{
                        width: `${(currentPage / totalPages) * 100}%`,
                        height: '100%',
                        background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                        borderRadius: '2px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>

                    {/* Load More Button */}
                    <button 
                      onClick={() => loadArticles(currentPage + 1, false)}
                      style={{
                        padding: isMobile ? '14px 28px' : '16px 32px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '16px',
                        cursor: 'pointer',
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        margin: '0 auto 16px auto'
                      }}
                    >
                      <Sparkles size={16} />
                      Load More Research
                    </button>

                    {/* Page Navigation */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      gap: '8px', 
                      marginTop: '16px',
                      flexDirection: isMobile ? 'column' : 'row'
                    }}>
                      {hasPrevPage && (
                        <button
                          onClick={() => loadArticles(currentPage - 1, true)}
                          style={{
                            padding: '8px 12px',
                            background: colors.bgCard,
                            border: '1px solid #333',
                            borderRadius: '8px',
                            color: '#888',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          ← Previous
                        </button>
                      )}
                      
                      <span style={{
                        padding: '8px 12px',
                        color: '#e5e5e5',
                        fontSize: '14px'
                      }}>
                        Page {currentPage} of {totalPages}
                      </span>
                      
                      {hasNextPage && (
                        <button
                          onClick={() => loadArticles(currentPage + 1, true)}
                          style={{
                            padding: '8px 12px',
                            background: colors.bgCard,
                            border: '1px solid #333',
                            borderRadius: '8px',
                            color: '#888',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Next →
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <span style={{ fontSize: isMobile ? '28px' : '32px', marginBottom: '8px', display: 'block' }}>🎉</span>
                    <h3 style={{
                      margin: '0 0 8px 0',
                      fontSize: isMobile ? '18px' : '20px',
                      fontWeight: '600',
                      color: '#e5e5e5'
                    }}>
                      You've seen all the research!
                    </h3>
                    <p style={{
                      margin: '0 0 16px 0',
                      color: '#888',
                      fontSize: '14px'
                    }}>
                      All {totalArticles} articles viewed. Submit new research to expand the knowledge base!
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Sidebar - Hidden on mobile */}
          {!isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <TrendingCard topics={trendingTopics} />
              <CommunityStatsCard />
              
              {/* Research Newsletter Signup */}
              <div style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                borderRadius: '16px',
                padding: '20px',
                border: '1px solid #334155',
                textAlign: 'center'
              }}>
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#e2e8f0'
                }}>
                  📧 Research Newsletter
                </h3>
                <p style={{
                  margin: '0 0 16px 0',
                  fontSize: '13px',
                  color: '#94a3b8',
                  lineHeight: '1.4'
                }}>
                  Get weekly summaries of the latest cannabis research delivered to your inbox
                </p>
                <input
                  type="email"
                  placeholder="Enter your email"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #475569',
                    borderRadius: '8px',
                    background: '#1e293b',
                    color: '#e2e8f0',
                    fontSize: '13px',
                    marginBottom: '12px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <button style={{
                  width: '100%',
                  padding: '10px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}>
                  Subscribe Free
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Filter Modal */}
        <MobileFilterModal />

        {showSubmitDialog && (
          <SubmitResearchDialog 
            isOpen={showSubmitDialog}
            categories2={categories}
            onClose={() => setShowSubmitDialog(false)}
            onSubmit={(data) => {
              console.log('New research submitted:', data);
            }}
          />
        )}
        {showProfileView && selectedUserId && (
          <PublicProfileView
            userId={selectedUserId}
            currentUserId={getCurrentUserId()}
            onClose={() => {
              setShowProfileView(false);
              setSelectedUserId(null);
            }}
          />
        )}
      </div>
      {showArticleDetail && selectedArticle && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: '#0a0a0a',
            overflow: 'hidden'
          }}
        >
          <div style={{
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}>
            <ResearchArticleDetail 
              handleOpenArticle={handleOpenArticle}
              article={selectedArticle}
              tags={tags}
              onBack={() => {
                setShowArticleDetail(false);
                setSelectedArticle(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchHub;